import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { collection, query, orderBy, onSnapshot, doc, where, getDocs, deleteDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { generateTicketImage, downloadTicketImage } from '../utils/ticketImageGenerator';
import { subscribeToEventSettings, getDefaultEventSettings } from '../utils/settingsService';
import Ticket from '../components/Ticket';
import './RegistrationsPage.css';
import Swal from 'sweetalert2';

// Icon Components
const HamburgerIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 12H21M3 6H21M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const DashboardIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M0 1C0 0.45 0.45 0 1 0H6C6.55 0 7 0.45 7 1V6C7 6.55 6.55 7 6 7H1C0.45 7 0 6.55 0 6V1ZM9 1C9 0.45 9.45 0 10 0H15C15.55 0 16 0.45 16 1V6C16 6.55 15.55 7 15 7H10C9.45 7 9 6.55 9 6V1ZM0 10C0 9.45 0.45 9 1 9H6C6.55 9 7 9.45 7 10V15C7 15.55 6.55 16 6 16H1C0.45 16 0 15.55 0 15V10ZM9 10C9 9.45 9.45 9 10 9H15C15.55 9 16 9.45 16 10V15C16 15.55 15.55 16 15 16H10C9.45 16 9 15.55 9 15V10Z" fill="currentColor"/>
  </svg>
);

const RegistrationsIcon = () => (
  <svg width="20" height="16" viewBox="0 0 20 16" fill="none">
    <path d="M0 0V16H20V0H0ZM2 2H18V14H2V2ZM4 4H16V6H4V4ZM4 8H16V10H4V8ZM4 12H12V14H4V12Z" fill="currentColor"/>
  </svg>
);

const TicketIcon = () => (
  <svg width="18" height="16" viewBox="0 0 18 16" fill="none">
    <path d="M0 2C0 1.45 0.45 1 1 1H17C17.55 1 18 1.45 18 2V4C17.45 4 17 4.45 17 5C17 5.55 17.45 6 18 6V8C18 8.55 17.55 9 17 9H1C0.45 9 0 8.55 0 8V6C0.55 6 1 5.55 1 5C1 4.45 0.55 4 0 4V2Z" fill="currentColor"/>
  </svg>
);

const CheckInIcon = () => (
  <svg width="14" height="16" viewBox="0 0 14 16" fill="none">
    <path d="M0 1V15H14V1H0ZM2 3H12V13H2V3ZM6 5H8V7H6V5ZM6 9H8V11H6V9Z" fill="currentColor"/>
  </svg>
);


const LogoutIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M6 14H2C1.45 14 1 13.55 1 13V3C1 2.45 1.45 2 2 2H6V0H2C0.9 0 0 0.9 0 2V14C0 15.1 0.9 16 2 16H6V14ZM11.59 10.59L9.59 12.59L11.18 14.18L15.36 10L11.18 5.82L9.59 7.41L11.59 9.41H5V11.41H11.59Z" fill="currentColor"/>
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M11.5 10H10.71L10.43 9.73C11.41 8.59 12 7.11 12 5.5C12 2.46 9.54 0 6.5 0C3.46 0 1 2.46 1 5.5C1 8.54 3.46 11 6.5 11C8.11 11 9.59 10.41 10.73 9.43L11 9.71V10.5L15 14.49L14.49 15L10.5 11ZM6.5 11C3.96 11 2 9.04 2 6.5C2 3.96 3.96 2 6.5 2C9.04 2 11 3.96 11 6.5C11 9.04 9.04 11 6.5 11Z" fill="currentColor"/>
  </svg>
);

const FilterIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M0 2H16V4H0V2ZM2 7H14V9H2V7ZM4 12H12V14H4V12Z" fill="currentColor"/>
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M8 0L3 5H6V11H10V5H13L8 0ZM0 13V15H16V13H0Z" fill="currentColor"/>
  </svg>
);

const MoreOptionsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="3" r="1.5" fill="currentColor"/>
    <circle cx="8" cy="8" r="1.5" fill="currentColor"/>
    <circle cx="8" cy="13" r="1.5" fill="currentColor"/>
  </svg>
);


const DeleteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M2 4H14M12 4V13C12 13.55 11.55 14 11 14H5C4.45 14 4 13.55 4 13V4M6 4V2C6 1.45 6.45 1 7 1H9C9.55 1 10 1.45 10 2V4M6 7V11M10 7V11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const SettingsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M10 12.5C11.38 12.5 12.5 11.38 12.5 10C12.5 8.62 11.38 7.5 10 7.5C8.62 7.5 7.5 8.62 7.5 10C7.5 11.38 8.62 12.5 10 12.5ZM15.92 6.42L14.5 5C14.5 3.62 13.38 2.5 12 2.5L10.58 1.08C9.2 1.08 8.08 2.2 8.08 3.58L6.66 5C5.28 5 4.16 6.12 4.16 7.5L2.74 8.92C2.74 10.3 3.86 11.42 5.24 11.42L6.66 12.84C6.66 14.22 7.78 15.34 9.16 15.34L10.58 16.76C11.96 16.76 13.08 15.64 13.08 14.26L14.5 12.84C15.88 12.84 17 11.72 17 10.34L18.42 8.92C18.42 7.54 17.3 6.42 15.92 6.42Z" fill="currentColor"/>
  </svg>
);

const BroadcastIcon = () => (
  <svg width="20" height="16" viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 2H18C19.1 2 20 2.9 20 4V12C20 13.1 19.1 14 18 14H2C0.9 14 0 13.1 0 12V4C0 2.9 0.9 2 2 2ZM18 4L10 8.5L2 4V4L10 0.5L18 4ZM2 6V12H18V6L10 10.5L2 6Z" fill="currentColor" />
  </svg>
);

function RegistrationsPage() {
  const navigate = useNavigate();
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [generatedTicket, setGeneratedTicket] = useState(null);
  const [generatedQRCode, setGeneratedQRCode] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useState(null); // Track which row's menu is open
  const [eventSettings, setEventSettings] = useState(getDefaultEventSettings());
  const ticketRef = useRef(null);
  const cleaningDeletedRef = useRef(new Set());
  const itemsPerPage = 10;

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          emailVerified: firebaseUser.emailVerified
        });
        fetchRegistrations();
      } else {
        navigate('/admin');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    const unsubscribeSettings = subscribeToEventSettings((settings) => {
      setEventSettings(settings);
    });

    return () => {
      if (unsubscribeSettings) {
        unsubscribeSettings();
      }
    };
  }, []);

  const fetchRegistrations = () => {
    try {
      const registrationsRef = collection(db, 'registrations');
      const q = query(registrationsRef, orderBy('registeredAt', 'desc'));

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const registrationsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setRegistrations(registrationsData);
        setLoading(false);
      }, (error) => {
        console.error('Error fetching registrations:', error);
        setLoading(false);
      });

      return unsubscribe;
    } catch (error) {
      console.error('Error setting up registrations listener:', error);
      setLoading(false);
    }
  };

  // Automatically purge any lingering documents that still have status "deleted"
  useEffect(() => {
    const deletedRegistrations = registrations.filter(reg => reg.status === 'deleted');
    if (!deletedRegistrations.length) {
      return;
    }

    const removeDeletedRecords = async () => {
      await Promise.all(
        deletedRegistrations.map(async (reg) => {
          if (cleaningDeletedRef.current.has(reg.id)) {
            return;
          }
          cleaningDeletedRef.current.add(reg.id);
          try {
            await deleteDoc(doc(db, 'registrations', reg.id));

            // Remove any tickets created for this user (by ticketId or email)
            const ticketsRef = collection(db, 'tickets');
            const ticketQuery = query(
              ticketsRef,
              where('userEmail', '==', reg.email || '')
            );
            const ticketSnapshot = await getDocs(ticketQuery);
            const ticketIdQuery = reg.ticketId
              ? query(ticketsRef, where('ticketId', '==', reg.ticketId))
              : null;

            const deletions = ticketSnapshot.docs.map((ticketDoc) =>
              deleteDoc(doc(db, 'tickets', ticketDoc.id))
            );

            if (ticketIdQuery) {
              const ticketIdSnapshot = await getDocs(ticketIdQuery);
              ticketIdSnapshot.forEach((ticketDoc) => {
                deletions.push(deleteDoc(doc(db, 'tickets', ticketDoc.id)));
              });
            }

            await Promise.all(deletions);
          } catch (cleanupError) {
            console.error('Error fully deleting registration:', cleanupError);
          } finally {
            cleaningDeletedRef.current.delete(reg.id);
          }
        })
      );
    };

    removeDeletedRecords();
  }, [registrations]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem('adminUser');
      sessionStorage.removeItem('adminUser');
      navigate('/admin');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Filter registrations
  const paidStatuses = ['paid', 'confirmed', 'ticket-sent', 'checked-in'];
  const filteredRegistrations = registrations.filter(reg => {
    if (reg.status === 'deleted') {
      return false;
    }
    const matchesSearch = 
      reg.fullName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      reg.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      reg.phoneNumber?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' 
      ? true 
      : statusFilter === 'paid'
        ? paidStatuses.includes(reg.status)
        : reg.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Pagination
  const totalPages = Math.ceil(filteredRegistrations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRegistrations = filteredRegistrations.slice(startIndex, endIndex);

  // Helper functions
  const getUserInitials = (name) => {
    if (!name) return 'U';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'paid': { text: 'Paid', bg: '#F0FDF4', color: '#15803D' },
      'confirmed': { text: 'Paid', bg: '#F0FDF4', color: '#15803D' },
      'pending': { text: 'Pending', bg: '#FEF9C3', color: '#A16207' },
      'checked-in': { text: 'Checked In', bg: '#F0FDF4', color: '#15803D' },
      'ticket-sent': { text: 'Ticket Sent', bg: '#EFF6FF', color: '#1D4ED8' }
    };
    return statusMap[status] || { text: status, bg: '#F3F4F6', color: '#374151' };
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDownloadTicket = async () => {
    if (!ticketRef.current || !generatedTicket) return;

    try {
      const ticketImage = await generateTicketImage(ticketRef.current, 'png', 0.92);
      downloadTicketImage(ticketImage, generatedTicket.ticketId, 'png');
    } catch (error) {
      console.error('Error downloading ticket:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error downloading ticket. Please try again.',
        confirmButtonColor: '#b91c1c'
      });
    }
  };

  const handleCloseTicketModal = () => {
    setShowTicketModal(false);
    setGeneratedTicket(null);
    setGeneratedQRCode(null);
  };


  const handleDeleteRegistration = async (registration) => {
    const confirmResult = await Swal.fire({
      icon: 'warning',
      title: 'Delete registration?',
      text: `Are you sure you want to delete ${registration.fullName}'s registration? This action cannot be undone.`,
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280'
    });

    if (!confirmResult.isConfirmed) {
      return;
    }

    try {
      // Permanently delete registration
      const registrationRef = doc(db, 'registrations', registration.id);
      await deleteDoc(registrationRef);

      // Delete associated ticket if it exists
      if (registration.ticketId) {
        try {
          const ticketsRef = collection(db, 'tickets');
          const q = query(ticketsRef, where('ticketId', '==', registration.ticketId));
          const querySnapshot = await getDocs(q);
          
          querySnapshot.forEach(async (ticketDoc) => {
            await deleteDoc(doc(db, 'tickets', ticketDoc.id));
          });
        } catch (ticketError) {
          console.error('Error updating ticket:', ticketError);
        }
      }

      setOpenActionMenu(null);
      await Swal.fire({
        icon: 'success',
        title: 'Deleted',
        text: 'Registration deleted successfully.',
        confirmButtonColor: '#10b981'
      });
    } catch (error) {
      console.error('Error deleting registration:', error);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error deleting registration. Please try again.',
        confirmButtonColor: '#b91c1c'
      });
    }
  };

  const toggleActionMenu = (registrationId) => {
    setOpenActionMenu(openActionMenu === registrationId ? null : registrationId);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const handleNavClick = (path) => {
    closeSidebar();
    navigate(path);
  };

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openActionMenu && !event.target.closest('.action-menu-container')) {
        setOpenActionMenu(null);
      }
    };

    if (openActionMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [openActionMenu]);

  // Calculate dropdown position to open upward if near bottom of screen
  useEffect(() => {
    if (!openActionMenu) {
      return;
    }

    const calculatePosition = () => {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        const container = document.querySelector(`[data-registration-id="${openActionMenu}"]`);
        if (!container) {
          // Retry after a short delay if element not found
          setTimeout(calculatePosition, 50);
          return;
        }

        const button = container.querySelector('.action-menu-button');
        if (!button) return;

        const rect = button.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        // const dropdownHeight = 120; // Approximate dropdown height with padding
        const spaceBelow = viewportHeight - rect.bottom;
        const buttonBottom = rect.bottom;
        
        if (buttonBottom > viewportHeight * 0.6 || spaceBelow < 150) {
          // You can add logic here if you decide to use dropdownOpenUpward state
        }
      });
    };

    // Calculate immediately and after a short delay to ensure DOM is ready
    calculatePosition();
    const timeoutId = setTimeout(calculatePosition, 10);
    
    const handleResize = () => calculatePosition();
    const handleScroll = () => calculatePosition();
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [openActionMenu]);

  return (
    <div className="registrations-page-container">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={closeSidebar}></div>
      )}
      
      <aside className={`dashboard-sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        {/* Close Button for Mobile */}
        <button className="sidebar-close-button" onClick={closeSidebar} aria-label="Close menu">
          <CloseIcon />
        </button>
        
        {/* Logo Section */}
        <div className="sidebar-logo">
        <div className="logo-icon">
            <img
              src={process.env.PUBLIC_URL + '/logo.jpg'}
              alt="Mountain Top University"
              className="admin-dashboard-logo-image"
            />
          </div>
          <div className="logo-text">
            <div className="logo-title">MTU Alumni</div>
            <div className="logo-subtitle">Event Management</div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          <div className="nav-item" onClick={() => handleNavClick('/admin/dashboard')}>
            <DashboardIcon />
            <span>Dashboard</span>
          </div>
          <div className="nav-item active" onClick={closeSidebar}>
            <RegistrationsIcon />
            <span>Registrations</span>
          </div>
          <div className="nav-item" onClick={() => handleNavClick('/admin/generate-ticket')}>
            <TicketIcon />
            <span>Generate Ticket</span>
          </div>
          <div className="nav-item" onClick={() => handleNavClick('/admin/checkin')}>
            <CheckInIcon />
            <span>Check-in</span>
          </div>
          <div className="nav-item" onClick={() => handleNavClick('/admin/broadcasts')}>
            <BroadcastIcon />
            <span>Email Broadcast</span>
          </div>
          <div className="nav-item" onClick={() => handleNavClick('/admin/settings')}>
            <SettingsIcon />
            <span>Settings</span>
          </div>
        </nav>

        {/* User Profile Section */}
        <div className="sidebar-user">
          <div className="user-info">
            <div className="user-avatar">
              {getUserInitials(user?.email || 'Admin User')}
            </div>
            <div className="user-details">
              <div className="user-name">Admin User</div>
              <div className="user-email">{user?.email || 'admin@mtu.edu'}</div>
            </div>
          </div>
          <button className="logout-button" onClick={handleLogout}>
            <LogoutIcon />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      <main className="registrations-main">
        {/* Header */}
        <header className="registrations-header">
          <div className="header-content">
            <div className="header-left">
              <h1 className="header-title">Registrations</h1>
              <p className="header-subtitle">Manage and view all event registrations</p>
            </div>
            <div className="header-right">
              <button className="hamburger-button" onClick={toggleSidebar} aria-label="Toggle menu">
                <HamburgerIcon />
              </button>
            </div>
          </div>
        </header>

        {/* Filters and Search */}
        <div className="registrations-filters">
          <div className="search-container">
            <SearchIcon />
            <input
              type="text"
              className="search-input"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
          <div className="filter-container">
            <FilterIcon />
            <select
              className="filter-select"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="confirmed">Confirmed</option>
              <option value="checked-in">Checked In</option>
              <option value="ticket-sent">Ticket Sent</option>
            </select>
          </div>
        </div>

        {/* Main Content */}
        <div className="registrations-content">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading registrations...</p>
            </div>
          ) : (
            <>
              {/* Summary Stats */}
              <div className="summary-stats">
                <div className="summary-stat">
                  <div className="summary-label">Total Registrations</div>
                  <div className="summary-value">{filteredRegistrations.length}</div>
                </div>
                <div className="summary-stat">
                  <div className="summary-label">Paid</div>
                  <div className="summary-value">
                    {filteredRegistrations.filter(r => paidStatuses.includes(r.status)).length}
                  </div>
                </div>
                <div className="summary-stat">
                  <div className="summary-label">Pending</div>
                  <div className="summary-value">
                    {filteredRegistrations.filter(r => r.status === 'pending').length}
                  </div>
                </div>
                <div className="summary-stat">
                  <div className="summary-label">Total Revenue</div>
                  <div className="summary-value">
                    ₦{filteredRegistrations
                      .filter(r => paidStatuses.includes(r.status))
                      .reduce((sum, r) => sum + (r.amount || 0), 0)
                      .toLocaleString()}
                  </div>
                </div>
              </div>

              {/* Registrations Table - Desktop */}
              <div className="registrations-table-card">
                <div className="table-container">
                  <table className="registrations-table">
                    <thead>
                      <tr>
                        <th>NAME</th>
                        <th>EMAIL</th>
                        <th>PHONE</th>
                        <th>GENDER</th>
                        <th>STATUS</th>
                        <th>AMOUNT</th>
                        <th>REGISTERED AT</th>
                        <th>ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedRegistrations.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="empty-state">
                            {searchQuery || statusFilter !== 'all' 
                              ? 'No registrations match your filters' 
                              : 'No registrations yet'}
                          </td>
                        </tr>
                      ) : (
                        paginatedRegistrations.map((reg) => {
                          const statusBadge = getStatusBadge(reg.status);
                          return (
                            <tr key={reg.id}>
                              <td>
                                <div className="user-cell">
                                  <div className="table-avatar">
                                    {getUserInitials(reg.fullName)}
                                  </div>
                                  <span>{reg.fullName}</span>
                                </div>
                              </td>
                              <td>
                                <span className="email-cell">{reg.email}</span>
                              </td>
                              <td>
                                <span className="phone-cell">{reg.phoneNumber || 'N/A'}</span>
                              </td>
                              <td>
                                <span className="gender-cell">{reg.gender || 'N/A'}</span>
                              </td>
                              <td>
                                <span className="status-badge" style={{ backgroundColor: statusBadge.bg, color: statusBadge.color }}>
                                  {statusBadge.text}
                                </span>
                              </td>
                              <td>
                                <span className="amount-cell">₦{reg.amount?.toLocaleString() || '0'}</span>
                              </td>
                              <td>
                                <span className="date-cell">{formatDate(reg.registeredAt)}</span>
                              </td>
                              <td>
                                <div className="action-buttons">
                                  {(reg.status === 'paid' || reg.status === 'confirmed') && (
                                    <div 
                                      className="action-menu-container"
                                      data-registration-id={reg.id}
                                    >
                                      <button 
                                        className="action-menu-button"
                                        onClick={() => toggleActionMenu(reg.id)}
                                      >
                                        <MoreOptionsIcon />
                                      </button>
                                      {openActionMenu === reg.id && (
                                        <div className="action-menu-dropdown open-upward">
                                          <button 
                                            className="action-menu-item delete"
                                            onClick={() => handleDeleteRegistration(reg)}
                                          >
                                            <DeleteIcon />
                                            <span>Delete</span>
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  {reg.status === 'checked-in' && (
                                    <span className="action-link">Checked In</span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="registrations-cards-mobile">
                  {paginatedRegistrations.length === 0 ? (
                    <div className="empty-state-mobile">
                      {searchQuery || statusFilter !== 'all' 
                        ? 'No registrations match your filters' 
                        : 'No registrations yet'}
                    </div>
                  ) : (
                    paginatedRegistrations.map((reg) => {
                      const statusBadge = getStatusBadge(reg.status);
                      return (
                        <div key={reg.id} className="registration-card-mobile">
                          <div className="card-header-mobile">
                            <div className="card-user-info">
                              <div className="card-avatar">
                                {getUserInitials(reg.fullName)}
                              </div>
                              <div className="card-user-details">
                                <div className="card-name">{reg.fullName}</div>
                                <div className="card-email">{reg.email}</div>
                              </div>
                            </div>
                            <div className="card-status-mobile">
                              <span className="status-badge" style={{ backgroundColor: statusBadge.bg, color: statusBadge.color }}>
                                {statusBadge.text}
                              </span>
                            </div>
                          </div>
                          <div className="card-body-mobile">
                            <div className="card-row">
                              <span className="card-label">Phone:</span>
                              <span className="card-value">{reg.phoneNumber || 'N/A'}</span>
                            </div>
                            <div className="card-row">
                              <span className="card-label">Gender:</span>
                              <span className="card-value">{reg.gender || 'N/A'}</span>
                            </div>
                            <div className="card-row">
                              <span className="card-label">Amount:</span>
                              <span className="card-value amount-cell">₦{reg.amount?.toLocaleString() || '0'}</span>
                            </div>
                            <div className="card-row">
                              <span className="card-label">Registered:</span>
                              <span className="card-value date-cell">{formatDate(reg.registeredAt)}</span>
                            </div>
                          </div>
                          <div className="card-actions-mobile">
                            {(reg.status === 'paid' || reg.status === 'confirmed') && (
                              <div 
                                className="action-menu-container"
                                data-registration-id={reg.id}
                              >
                                <button 
                                  className="action-menu-button"
                                  onClick={() => toggleActionMenu(reg.id)}
                                >
                                  <MoreOptionsIcon />
                                </button>
                                {openActionMenu === reg.id && (
                                  <div className="action-menu-dropdown mobile-dropdown open-upward">
                                    <button 
                                      className="action-menu-item delete"
                                      onClick={() => handleDeleteRegistration(reg)}
                                    >
                                      <DeleteIcon />
                                      <span>Delete</span>
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                            {reg.status === 'checked-in' && (
                              <span className="action-link checked-in-mobile">Checked In</span>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="pagination">
                    <div className="pagination-info">
                      Showing {startIndex + 1} to {Math.min(endIndex, filteredRegistrations.length)} of {filteredRegistrations.length} registrations
                    </div>
                    <div className="pagination-controls">
                      <button
                        className="pagination-button"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeftIcon />
                        <span>Previous</span>
                      </button>
                      <div className="pagination-numbers">
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                          .filter(page => {
                            if (totalPages <= 7) return true;
                            if (page === 1 || page === totalPages) return true;
                            if (Math.abs(page - currentPage) <= 1) return true;
                            return false;
                          })
                          .map((page, index, array) => {
                            const prevPage = array[index - 1];
                            const showEllipsis = prevPage && page - prevPage > 1;
                            return (
                              <React.Fragment key={page}>
                                {showEllipsis && <span className="pagination-ellipsis">...</span>}
                                <button
                                  className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                                  onClick={() => setCurrentPage(page)}
                                >
                                  {page}
                                </button>
                              </React.Fragment>
                            );
                          })}
                      </div>
                      <button
                        className="pagination-button"
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                      >
                        <span>Next</span>
                        <ChevronRightIcon />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </main>

      {/* Ticket Modal */}
      {showTicketModal && generatedTicket && (
        <div className="ticket-modal-overlay" onClick={handleCloseTicketModal}>
          <div className="ticket-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="ticket-modal-header">
              <h2>Ticket Generated Successfully!</h2>
              <button className="ticket-modal-close" onClick={handleCloseTicketModal}>
                <CloseIcon />
              </button>
            </div>
            <div className="ticket-modal-body">
              <div ref={ticketRef}>
                <Ticket
                  ticketData={generatedTicket}
                  qrCodeDataUrl={generatedQRCode}
                  eventSettings={eventSettings}
                />
              </div>
            </div>
            <div className="ticket-modal-footer">
              <button className="ticket-download-button" onClick={handleDownloadTicket}>
                <DownloadIcon />
                <span>Download Ticket</span>
              </button>
              <button className="ticket-close-button" onClick={handleCloseTicketModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RegistrationsPage;

